import { Component, OnInit, ViewChild } from '@angular/core'
import { LiveAnnouncer } from '@angular/cdk/a11y'
import { MatSort } from '@angular/material/sort'
import { ActivatedRoute } from '@angular/router'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { MatPaginator, PageEvent } from '@angular/material/paginator'
import { MatTable, MatTableDataSource } from '@angular/material/table'
import { CategoriesService, categoriesTableColumns } from '../categories.service'
import { VendorService } from 'src/app/multivendor/vendor.service'
import { LoginService } from 'src/app/login/login.service'
import { ShopifyService } from 'src/app/integrations/shopify/shopify.service'

@Component({
  selector: 'app-categories',
  templateUrl: './categories.component.html',
  styleUrls: ['./categories.component.scss']
})
export class CategoriesComponent implements OnInit {
  mobile = false
  public Math = Math

  @ViewChild(MatPaginator) paginator!: MatPaginator
  @ViewChild(MatTable)
  table!: MatTable<categoriesTableColumns>

  @ViewChild(MatSort, { static: true })
  matSort!: MatSort
  selectedFile: File | null = null
  showUploadProgress: any = []
  expandedCategories: boolean[] = []
  editingCategoryIndex = -1
  public displayedCategories: any[] = []
  public dataSource = new MatTableDataSource<any>([])
  public categories: any[] = []
  pageSize = 5
  pageIndex = 0
  saveState = false
  showDeleteModal = false
  private pendingDelete: { type: 'category'; index: number } | { type: 'subcategory'; cat: any; index: number } | null = null

  // Summary cards shown above the list, same as orders page
  categorySummary = [
    { title: 'Categories', count: 0, icon: 'categories_icon.svg' },
    { title: 'Sub-categories', count: 0, icon: 'category.svg' }
  ]

  get deleteItemName(): string {
    if (!this.pendingDelete) return ''
    if (this.pendingDelete.type === 'category') {
      const actual = this.pageIndex * this.pageSize + this.pendingDelete.index
      return this.categories[actual]?.categoryName || 'this category'
    }
    return this.pendingDelete.cat.subcategories[this.pendingDelete.index]?.subcategory || 'this sub-category'
  }

  get deleteItemType(): string {
    return this.pendingDelete?.type === 'subcategory' ? 'Sub-Category' : 'Category'
  }

  openDeleteModal(type: 'category', index: number): void
  openDeleteModal(type: 'subcategory', cat: any, index: number): void
  openDeleteModal(type: 'category' | 'subcategory', catOrIndex: any, index?: number): void {
    if (type === 'category') {
      this.pendingDelete = { type, index: catOrIndex }
    } else {
      this.pendingDelete = { type, cat: catOrIndex, index: index! }
    }
    this.showDeleteModal = true
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false
    this.pendingDelete = null
  }

  confirmDelete(): void {
    if (!this.pendingDelete) return
    if (this.pendingDelete.type === 'category') {
      this.removeItem(this.pendingDelete.index)
    } else {
      this.removeSub(this.pendingDelete.cat, this.pendingDelete.index)
    }
    this.closeDeleteModal()
  }

  constructor(public loginService: LoginService,private categoriesService: CategoriesService, private _liveAnnouncer: LiveAnnouncer, private route: ActivatedRoute, public builderService: BuilderService,public vendor:VendorService, private shopifyService: ShopifyService) {}

  ngOnInit(): void {
    this.categoriesService.getCategories().subscribe((categories: any) => {
      this.categories = categories
      this.updateDisplayedCategories()
      this.dataSource.data = this.categories
    })

    if (window.screen.width <= 768) {
      // 768px portrait
      this.mobile = true
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.matSort
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize
    this.pageIndex = event.pageIndex
    this.updateDisplayedCategories()
  }

  changePageSize(newSize: string | number) {
    const size = typeof newSize === 'string' ? parseInt(newSize, 10) : newSize
    this.pageSize = size
    this.pageIndex = 0
    if (this.paginator) {
      this.paginator.pageSize = size
      this.paginator.pageIndex = 0
    }
    this.updateDisplayedCategories()
  }

  previousPage(): void {
    if (this.pageIndex > 0) {
      this.pageIndex--
      this.updateDisplayedCategories()
      if (this.paginator) {
        this.paginator.pageIndex = this.pageIndex
      }
    }
  }

  nextPage(): void {
    const totalPages = Math.ceil(this.categories.length / this.pageSize)
    if (this.pageIndex < totalPages - 1) {
      this.pageIndex++
      this.updateDisplayedCategories()
      if (this.paginator) {
        this.paginator.pageIndex = this.pageIndex
      }
    }
  }

  toggleExpand(index: number) {
    this.expandedCategories[index] = !this.expandedCategories[index]
  }

  handleExpandClick(index: number) {
    this.toggleExpand(index)
  }

  focusCategoryName(index: number) {
    this.editingCategoryIndex = index
    this.saveState = true
    setTimeout(() => {
      const inputs = document.querySelectorAll('.category-name-input')
      if (inputs[index]) {
        (inputs[index] as HTMLInputElement).focus()
      }
    }, 50)
  }

  activateSave() {
    this.saveState = true
  }

  removeItem(index: number) {
    const actualIndex = this.pageIndex * this.pageSize + index
    const cat = this.categories[actualIndex]
    if (!cat.isNew) {
      this.categoriesService.deleteCategory(cat.categoryId)
    }
    this.categories.splice(actualIndex, 1)
    this.updateDisplayedCategories()
  }

  removeSub(cat: any, index: number) {
    const sub = cat.subcategories[index]
    if (!sub.isNew) {
      this.categoriesService.deleteCategory(sub.subCategoryId)
    }
    cat.subcategories.splice(index, 1)
    this.updateDisplayedCategories()
  }

  addSub(cat: any) {
    if (!cat.subcategories) {
      cat.subcategories = []
    }
    cat.subcategories.push({ subCategoryId: '', subcategory: '', categoryImage: '', isNew: true })
    this.saveState = true
    this.updateDisplayedCategories()
  }

  addCategory() {
    const newCategory = {
      categoryId: '',
      categoryName: '',
      categoryImage: '',
      subcategories: [],
      isNew: true
    }
    if (!Array.isArray(this.categories)) {
      this.categories = []
    }

    this.categories.unshift(newCategory)
    this.saveState = true
    this.updateDisplayedCategories()
    this.expandedCategories[0] = true
    this.editingCategoryIndex = 0
    if (this.pageIndex === 0) {
      this.displayedCategories = this.categories.slice(0, this.pageSize)
    }
    setTimeout(() => {
      const inputs = document.querySelectorAll('.category-name-input')
      if (inputs[0]) {
        (inputs[0] as HTMLInputElement).focus()
      }
    }, 100)
  }

  onCategoryNameBlur(cat: any) {
    if (!cat.categoryName?.trim()) return
    const sortOrder = this.categories.indexOf(cat)
    if (cat.isNew) {
      this.categoriesService.insertCategory(cat, sortOrder).then((id) => {
        if (id) { cat.categoryId = id; cat.isNew = false; this.syncCategoryToShopify(cat) }
      })
    } else {
      this.categoriesService.updateCategory(cat, sortOrder)
      this.syncCategoryToShopify(cat)
    }
  }

  private syncCategoryToShopify(cat: any): void {
    if (!this.shopifyService.shopifyConnected) return
    this.shopifyService.pushCollections([{
      categoryId: cat.categoryId,
      categoryName: cat.categoryName,
      categoryImage: cat.categoryImage || '',
      shopify_id: cat.external_id || null,
    }]).subscribe({
      error: (err: any) => console.error('Shopify category sync failed:', err)
    })
  }

  onSubCategoryBlur(sub: any, cat: any) {
    const name = sub.subcategory?.trim()
    if (!name || cat.isNew) return  // parent must exist in DB first
    const sortOrder = cat.subcategories.indexOf(sub)
    if (sub.isNew) {
      this.categoriesService.insertSubCategory(sub, cat.categoryId, sortOrder).then((id) => {
        if (id) { sub.subCategoryId = id; sub.isNew = false }
      })
    } else {
      this.categoriesService.updateSubCategory(sub, sortOrder)
    }
  }

  updateDisplayedCategories() {
    const startIndex = this.pageIndex * this.pageSize
    const endIndex = startIndex + this.pageSize
    this.displayedCategories = this.categories.slice(startIndex, endIndex)
    this.updateSummaryCounts()
  }

  updateSummaryCounts(): void {
    const subCount = this.categories.reduce((total, cat) => total + (cat.subcategories?.length || 0), 0)
    this.categorySummary[0].count = this.categories.length
    this.categorySummary[1].count = subCount
  }

  saveCategories() {
    for (const cat of this.categories) {
      if (cat.categoryName?.trim()) this.onCategoryNameBlur(cat)
      for (const sub of cat.subcategories || []) {
        if (sub.subcategory?.trim()) this.onSubCategoryBlur(sub, cat)
      }
    }
    this.editingCategoryIndex = -1
    this.saveState = false
    this.categoriesService.submitCategory(this.categories).subscribe()
  }

  onCatFileSelected(event: any, cat: any, index: number, subIndex: number, type = 'category') {
    this.selectedFile = event.target.files[0] as File
    if (!this.selectedFile) return
    if (type === 'category') this.showUploadProgress[index] = true

    this.builderService.uploadImage(this.selectedFile, this.builderService.selectedProject, 'categories')?.subscribe((response) => {
      cat.categoryImage = response
      if (type === 'category') {
        this.showUploadProgress[index] = false
        if (!cat.isNew) {
          const sortOrder = this.categories.indexOf(cat)
          this.categoriesService.updateCategory(cat, sortOrder)
        }
      } else {
        // subcategory image — cat here is the sub object
        if (!cat.isNew) {
          const parentCat = this.categories.find((c: any) => c.subcategories?.includes(cat))
          if (parentCat) {
            const sortOrder = parentCat.subcategories.indexOf(cat)
            this.categoriesService.updateSubCategory(cat, sortOrder)
          }
        }
      }
    })
  }

  removeImg(cat: any) {
    cat.categoryImage = ''
    this.activateSave()
  }

  removeSubImg(sub: any) {
    sub.categoryImage = ''
    this.activateSave()
  }

  generateUniqueId = () => {
    const timestamp = new Date().getTime().toString()
    const random = Math.floor(Math.random() * 10000).toString()
    return timestamp + '-' + random
  }
}
